import * as UI from '@brewlogix/blx-ui'
import { AsyncSelect, components, DropdownIndicatorProps } from 'chakra-react-select'
import classNames from 'classnames'
import mapboxgl from 'mapbox-gl'
import React from 'react'
import { Location } from 'src/types/schema/graphql'
import { asyncSelectConfig } from '../../config/asyncSelectConfig'
import { useFilterStore } from '../../state/filterStore'
import { mapStore } from '../../state/mapStore'
import useMapFunctions from '../../utils/functions/useMapFunctions'
import { formatOptionLabel } from './functions/formatOptionLabel'
import { parseLocations } from './functions/parseLocations'
import { parseMapboxPlaces } from './functions/parseMapboxPlaces'

interface IBreweriesForYouSearch {
    mapRef: mapboxgl.Map
    popupRef: React.MutableRefObject<mapboxgl.Popup>
}

const BreweriesForYouSearch: React.FC<IBreweriesForYouSearch> = ({ mapRef, popupRef }) => {
    // Map Store
    const fullScreenShowFullMap = mapStore((state) => state.fullScreenShowFullMap)
    const setCurrentLocation = mapStore((state) => state.setCurrentLocation)
    const setLocationQuery = mapStore((state) => state.setLocationQuery)
    const suggestedAddresses = mapStore((state) => state.suggestedAddresses)
    const wildcardText = mapStore((state) => state.wildcardText)
    const searchAsMapMoves = mapStore((state) => state.searchAsMapMoves)
    const setResultsLoaded = mapStore((state) => state.setResultsLoaded)
    const radius = mapStore((state) => state.radius)
    const setUserLocationState = mapStore((state) => state.setUserLocationState)
    const selectedWildcardOption = mapStore((state) => state.selectedWildcardOption)
    const setSelectedWildcardOption = mapStore((state) => state.setSelectedWildcardOption)
    const locations = mapStore((state) => state.locations)
    const defaultCombinedData = mapStore((state) => state.defaultCombinedData)
    const setDefaultCombinedData = mapStore((state) => state.setDefaultCombinedData)
    const setDefaultCombinedRouteInputData = mapStore(
        (state) => state.setDefaultCombinedRouteInputData
    )
    const setPopupData = mapStore((state) => state.setPopupData)

    // Filter Store
    const stateFilter = useFilterStore((state) => state.stateFilter)
    const brewFilterOptions = useFilterStore((state) => state.brewFilterOptions)
    const patronFilterOptions = useFilterStore((state) => state.patronFilterOptions)
    const foodFilterOptions = useFilterStore((state) => state.foodFilterOptions)
    const seatingFilterOptions = useFilterStore((state) => state.seatingFilterOptions)
    const setShowFiltersDrawer = useFilterStore((state) => state.setShowFiltersDrawer)
    const filterCount = useFilterStore((state) => state.filterCount)
    const openNowHours = useFilterStore((state) => state.openNowHours)

    const { throttledSearchLocations } = useMapFunctions()

    React.useEffect(() => {
        const initialLocations = parseLocations(locations.slice(0, 5))
        const initialMapboxPlaces = parseMapboxPlaces(suggestedAddresses)
        setDefaultCombinedData([...initialLocations, ...initialMapboxPlaces])
        setDefaultCombinedRouteInputData([...initialLocations, ...initialMapboxPlaces])
    }, [locations])

    const handleOptionClick = (location: Location) => {
        if (
            location?.title &&
            location?.slug &&
            location?.address?.geo?.lat &&
            location?.address?.geo?.lon &&
            location?.brand?.title &&
            location?.brand?.slug &&
            location?.brand?.assets
        )
            setPopupData({
                location,
            })
    }

    const handleSearchPlacesSelection = async (e: {
        label: string
        value: string
        coordinates: number[]
        location?: Location
    }) => {
        setCurrentLocation(e && e.value)
        setLocationQuery(e && e.value)
        setSelectedWildcardOption(e)
        if (!e || !e.value) {
            setCurrentLocation('Anywhere')
        }
        if (e && e.coordinates) {
            setUserLocationState({ lat: e.coordinates[1], lng: e.coordinates[0] })
            mapRef.panTo([e.coordinates[0], e.coordinates[1]], { duration: 100 })
            ;(() =>
                setTimeout(async () => {
                    const bounds = mapRef.getBounds()
                    await throttledSearchLocations({
                        wildcardText,
                        stateFilter,
                        brewFilterOptions,
                        patronFilterOptions,
                        foodFilterOptions,
                        seatingFilterOptions,
                        radius,
                        viewport: {
                            latitude: e.coordinates[1],
                            longitude: e.coordinates[0],
                        },
                        searchAsMapMoves,
                        bounds,
                        locationHours: {
                            currentlyOpen: openNowHours,
                        },
                    }).then(() => {
                        setResultsLoaded(true)
                    })
                }, 100))()
        }
        handleOptionClick(e.location)
    }

    const DropdownIndicator = (props: DropdownIndicatorProps) => {
        return (
            <components.DropdownIndicator {...props}>
                <UI.Icon theme='default' slug='HeroIconsSolid.Search' />
            </components.DropdownIndicator>
        )
    }
    const { breweriesForYouConfig } = asyncSelectConfig

    return (
        <div className='flex items-center space-x-3 px-4'>
            <AsyncSelect
                {...breweriesForYouConfig}
                className={classNames(
                    'my-4 w-full lg:w-[17rem]',
                    fullScreenShowFullMap ? 'xl:w-[19.75rem]' : 'xl:w-full'
                )}
                onChange={(e: {
                    label: string
                    value: string
                    coordinates: number[]
                    location?: Location
                }) => handleSearchPlacesSelection(e)}
                formatOptionLabel={formatOptionLabel}
                components={{ DropdownIndicator }}
                value={selectedWildcardOption}
                defaultOptions={defaultCombinedData}
            />

            <UI.Button
                onClick={() => setShowFiltersDrawer(true)}
                customClasses={classNames(
                    'px-2 hidden lg:flex',
                    filterCount > 0 ? 'border-blue-300' : 'border-dark',
                    fullScreenShowFullMap ? '' : 'xl:hidden'
                )}
                theme={filterCount > 0 ? 'link' : 'default'}
            >
                <UI.Icon slug='HeroIcons.Adjustments' customClasses='mr-1' theme='link' />
                Filter
                {filterCount > 0 && (
                    <span className='ml-1 h-4 w-4 rounded-full bg-blue-300 text-xs text-light'>
                        {filterCount}
                    </span>
                )}
            </UI.Button>
        </div>
    )
}

export default BreweriesForYouSearch
